import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import WhatsAppButton from "@/components/WhatsAppButton";
import ContactForm from "@/components/ContactForm";
import ProcessStep from "@/components/ProcessStep";
import { ArrowRight, Heart, ClipboardCheck } from "lucide-react";
import TechnologySection from "@/components/TechnologySection";
import FAQSection from "@/components/FAQSection";
import USPStrip from "@/components/USPStrip";
import FilteringFunnel from "@/components/FilteringFunnel";
import FoundersPledge from "@/components/FoundersPledge";
import VerifiedBadge from "@/components/VerifiedBadge";
import ChatBot from "@/components/ChatBot";

import SkipToContent from "@/components/SkipToContent";
import heroImage from "@/assets/hero-women.jpg";

const WomenPage = () => {
  const processSteps = [
    {
      number: 1,
      title: "הבית הרגשי",
      description:
        "את עוברת בירור עומק של הצרכים הרגשיים שלך. מה גורם לך להרגיש בטוחה? מה את באמת צריכה מזוגיות?",
    },
    {
      number: 2,
      title: "תקשורת בונה",
      description:
        "את מזהה דפוסים ומשפרת את היכולת לנהל זוגיות מיטיבה. את מקבלת כלים פרקטיים.",
    },
    {
      number: 3,
      title: "החיבור",
      description:
        "את מקבלת התאמה מדויקת לאדם שמחפש בדיוק את מה שיש לך לתת. שידוך מלווה ומוגן.",
    },
  ];

  return (
    <>
      <Helmet>
        <title>שידוכים לנשים | אמון בזוגיות</title>
        <meta
          name="description"
          content="שירות שידוכים פרימיום לנשים. התאמה מדויקת באמצעות אלגוריתם ומאמן אישי. שיחת התאמה חינם."
        />
      </Helmet>

      <SkipToContent />

      <div className="min-h-screen">
        {/* Hero Section */}
        <section aria-labelledby="hero-heading" className="relative min-h-[90vh] flex items-center">
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `url(${heroImage})` }}
            role="img"
            aria-hidden="true"
          >
            <div className="absolute inset-0 bg-gradient-to-l from-navy/95 via-navy/75 to-navy/50" />
          </div>

          <div className="relative z-10 container mx-auto px-6">
            {/* Header */}
            <header role="banner" className="absolute top-0 right-0 left-0 py-8 flex flex-col items-center gap-4">
              <Logo variant="light" />
              <nav aria-label="ניווט ראשי" className="flex items-center gap-6">
                <Link
                  to="/eva-protocol"
                  className="text-gold hover:text-gold-light transition-colors text-sm font-medium"
                >
                  פרוטוקול EVA
                </Link>
                <Link
                  to="/"
                  className="text-cream/70 hover:text-cream transition-colors flex items-center gap-2 text-sm"
                >
                  חזרה לעמוד הראשי
                  <ArrowRight className="w-4 h-4" aria-hidden="true" />
                </Link>
              </nav>
            </header>

            {/* Hero Content */}
            <main id="main-content" role="main" tabIndex={-1} className="max-w-2xl pt-32">
              <h1 id="hero-heading" className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-cream mb-6 leading-tight animate-fade-up opacity-0">
                מפסיקים להמר על הזמן שלך.
                <br />
                מחברים אותך לזוגיות מדויקת.
              </h1>
              <p className="text-lg md:text-xl text-cream/90 mb-8 leading-relaxed animate-fade-up opacity-0 delay-200">
                בזמן שהאפליקציות מוכרות כמות ורעש, אנחנו מתמקדים באיכות. המאגר
                היחיד שבו כל התאמה עוברת סינון כפול: אלגוריתם ומאמן אישי.
              </p>
              <div className="flex flex-col sm:flex-row items-start gap-4 animate-fade-up opacity-0 delay-300">
                <a href="#contact">
                  <Button variant="hero-primary" size="xl">
                    לשיחת ייעוץ - התחילי עכשיו
                  </Button>
                </a>
                <VerifiedBadge variant="dark" size="lg" />
              </div>
              <p className="text-cream/70 text-sm mt-3 animate-fade-up opacity-0 delay-400">
                ללא התחייבות
              </p>
            </main>
          </div>
        </section>

        {/* Quiz CTA Section */}
        <section aria-labelledby="quiz-heading" className="py-12 bg-cream-dark">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto bg-gradient-to-l from-cream via-cream-dark to-cream rounded-2xl p-8 md:p-12 border border-gold/30 shadow-elegant">
              <div className="flex flex-col md:flex-row items-center gap-8">
                <div className="flex-shrink-0">
                  <div className="w-20 h-20 rounded-full bg-gold/20 flex items-center justify-center">
                    <ClipboardCheck className="w-10 h-10 text-gold-dark" aria-hidden="true" />
                  </div>
                </div>
                <div className="flex-1 text-center md:text-right">
                  <h2 id="quiz-heading" className="font-serif text-2xl md:text-3xl font-bold text-navy mb-3">
                    גלי את ה-DNA הזוגי שלך
                  </h2>
                  <p className="text-navy/80 text-lg">
                    למה את מושכת את הגברים הלא נכונים? מבדק אבחון קצר.
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <Link to="/quiz-women">
                    <Button className="h-14 px-8 text-lg font-bold bg-navy hover:bg-navy-light text-cream shadow-elegant transition-all duration-300 hover:scale-105">
                      גלי את הדפוס שלך - חינם
                      <ArrowRight className="mr-2 w-5 h-5 rotate-180" aria-hidden="true" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* USP Strip */}
        <USPStrip variant="dark" />

        {/* Problem/Promise Section */}
        <section aria-labelledby="problem-heading" className="py-20 md:py-32 bg-navy">
          <div className="container mx-auto px-6">
            <div className="max-w-3xl mx-auto text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gold/20 mb-8">
                <Heart className="w-8 h-8 text-gold" aria-hidden="true" />
              </div>
              <h2 id="problem-heading" className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-cream mb-8">
                כמות לא שווה איכות.
              </h2>
              <p className="text-lg md:text-xl text-cream/90 leading-relaxed">
                נמאס לך לצאת עם גברים שלא יודעים מה הם רוצים?{" "}
                <span className="text-gold font-semibold">את לא צריכה להתפשר.</span>{" "}
                כל גבר במאגר שילם ועבר תהליך כדי להיות כאן. אין משחקים.
              </p>
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section aria-labelledby="process-heading" className="py-20 md:py-32 bg-navy-light">
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <h2 id="process-heading" className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-cream mb-4">
                תהליך שמחזיר לך שליטה
              </h2>
              <p className="text-xl text-cream/70">3 שלבי עבודה</p>
            </div>

            <div className="max-w-3xl mx-auto space-y-12">
              {processSteps.map((step, index) => (
                <div
                  key={index}
                  className="animate-fade-up opacity-0"
                  style={{ animationDelay: `${index * 150}ms` }}
                >
                  <ProcessStep {...step} variant="women" />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Technology Section */}
        <TechnologySection variant="women" />

        {/* Filtering Funnel */}
        <FilteringFunnel variant="women" />

        {/* FAQ Section */}
        <FAQSection variant="women" />

        {/* Founder's Pledge */}
        <FoundersPledge variant="women" />

        {/* Contact Section */}
        <section id="contact" aria-labelledby="contact-heading" className="py-20 md:py-32 bg-cream">
          <div className="container mx-auto px-6">
            <div className="max-w-xl mx-auto text-center">
              <h2 id="contact-heading" className="font-serif text-3xl md:text-4xl font-bold text-navy mb-4">
                מוכנה להתחיל?
              </h2>
              <p className="text-lg text-muted-foreground mb-10">
                השאירי פרטים ונחזור אלייך לשיחת התאמה אישית
              </p>
              <ContactForm buttonText="צרי קשר לשיחת התאמה" variant="women" />
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer role="contentinfo" className="bg-navy py-8">
          <div className="container mx-auto px-6 text-center">
            <nav aria-label="קישורי פוטר" className="mb-4">
              <ul className="flex justify-center gap-6 text-sm text-cream/60">
                <li>
                  <Link to="/privacy" className="hover:text-gold transition-colors">
                    מדיניות פרטיות
                  </Link>
                </li>
                <li>
                  <Link to="/accessibility" className="hover:text-gold transition-colors">
                    הצהרת נגישות
                  </Link>
                </li>
              </ul>
            </nav>
            <p className="text-cream/50 text-sm">
              © {new Date().getFullYear()} אמון בזוגיות. כל הזכויות שמורות.
            </p>
          </div>
        </footer>

        <WhatsAppButton />
        <ChatBot />
      </div>
    </>
  );
};

export default WomenPage;
